import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, RefreshCw, AlertTriangle, Pill, ClipboardCheck, ShieldAlert, CalendarClock, UserCheck, HeartPulse, Activity, Flag, ScrollText, Download } from "lucide-react";
import DailyNotesExportDialog from "@/components/DailyNotesExportDialog";
import CareFlagsPills, { CARE_FLAG_META, CARE_FLAG_ORDER } from "@/components/CareFlagsPills";
import { asArray, ALLERGY_SEVERITY_STYLES, getAllergySeverity } from "@/lib/clientFields";
import { computeReviewStatus, REVIEW_STATUS_STYLE, PRIORITY_STYLE, PROGRESS_STYLE } from "@/lib/carePlanConfig";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CLIENT_SECTIONS, CLIENT_SECTION_LIST } from "@/config/clientSections";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import RecordDialog from "@/components/RecordDialog";
import StatusPill from "@/components/StatusPill";
import FileAttachments from "@/components/FileAttachments";
import PersonAvatar from "@/components/PersonAvatar";
import PhotoUploader from "@/components/PhotoUploader";
import ClientGallery from "@/components/ClientGallery";
import ClientOutcomesPanel from "@/components/ClientOutcomesPanel";
import ClientSiteMembership from "@/components/ClientSiteMembership";
import ImportantPeopleView from "@/pages/client-sections/ImportantPeopleView";
import CarePlanView from "@/pages/client-sections/CarePlanView";
import { useAuth } from "@/context/AuthContext";

function CareFlagsEditor({ client, onSaved }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dnrOpen, setDnrOpen] = useState(false);
  const [dnrTyped, setDnrTyped] = useState("");
  const [dnrFile, setDnrFile] = useState(null);
  const [dnrNotes, setDnrNotes] = useState("");
  const [dnrUploading, setDnrUploading] = useState(false);
  const canManage = client?.id;
  const openMe = () => {
    setPending(client?.care_flags || []);
    setDnrTyped("");
    setDnrFile(null);
    setDnrNotes("");
    setOpen(true);
  };
  const toggle = (k) => setPending((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const uploadDnrFile = async (file) => {
    setDnrUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("module", "clients");
      fd.append("record_id", client.id);
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDnrFile({ id: r.data.id, filename: r.data.filename });
      toast.success("Signed order uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDnrUploading(false); }
  };

  const save = async () => {
    const prev = client?.care_flags || [];
    const dnrNewly = pending.includes("dnr") && !prev.includes("dnr");
    if (dnrNewly && !dnrOpen) { setDnrOpen(true); return; }
    if (dnrNewly) {
      if (!dnrTyped.trim()) { toast.error("Type the service user's full name to evidence the DNR"); return; }
      if (dnrTyped.trim().toLowerCase() !== (client.full_name || "").trim().toLowerCase()) {
        toast.error("Typed name doesn't match the service user's name on file"); return;
      }
    }
    setSaving(true);
    try {
      const payload = { care_flags: pending };
      if (dnrNewly) {
        payload.dnr_evidence = {
          typed_client_name: dnrTyped.trim(),
          evidence_file_id: dnrFile?.id || null,
          notes: dnrNotes.trim() || null,
        };
      }
      const r = await api.patch(`/clients/${client.id}/care-flags`, payload);
      toast.success("Care flags updated");
      onSaved(r.data);
      setOpen(false);
      setDnrOpen(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  if (!canManage) return null;
  return (
    <>
      <button
        onClick={openMe}
        data-testid="btn-edit-care-flags"
        className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
        title="Edit care flags"
      >
        <Flag className="w-3 h-3" /> Edit flags
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDnrOpen(false); }}>
        <DialogContent data-testid="care-flags-dialog">
          <DialogHeader>
            <DialogTitle>Care flags</DialogTitle>
            <DialogDescription>Tick every hazard that carers must be aware of the moment they open this profile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {CARE_FLAG_ORDER.map((k) => {
              const m = CARE_FLAG_META[k];
              const checked = pending.includes(k);
              const Icon = m.Icon;
              return (
                <label key={k} htmlFor={`flag-${k}`} className={`flex items-center gap-3 border p-2 cursor-pointer ${checked ? m.color : "border-slate-200 bg-white"}`} data-testid={`flag-row-${k}`}>
                  <Checkbox id={`flag-${k}`} checked={checked} onCheckedChange={() => toggle(k)} data-testid={`flag-checkbox-${k}`} />
                  <Icon className="w-4 h-4" strokeWidth={2.25} />
                  <div className="text-sm font-semibold flex-1">
                    {m.label}{m.full ? <span className="text-[11px] font-normal text-slate-500 ml-2">({m.full})</span> : null}
                  </div>
                </label>
              );
            })}
          </div>

          {dnrOpen && (
            <div className="mt-2 border-2 border-sky-400 bg-sky-50 p-3 space-y-3" data-testid="dnr-evidence-panel">
              <div className="text-sm font-semibold text-sky-900 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" /> DNR evidence required
              </div>
              <div className="text-xs text-slate-700">
                Do Not Resuscitate is a legal instruction. To evidence this flag, type <span className="font-semibold">{client?.full_name}</span> below and (recommended) attach the signed DNR order.
              </div>
              <div>
                <Label htmlFor="dnr-typed" className="text-xs">Type the service user&apos;s full name</Label>
                <Input
                  id="dnr-typed"
                  value={dnrTyped}
                  onChange={(e) => setDnrTyped(e.target.value)}
                  placeholder={client?.full_name || "Full name"}
                  data-testid="dnr-typed-name"
                />
              </div>
              <div>
                <Label className="text-xs">Signed DNR order (PDF/JPG, optional)</Label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => e.target.files?.[0] && uploadDnrFile(e.target.files[0])}
                  data-testid="dnr-file-input"
                  className="block w-full text-xs mt-1"
                />
                {dnrUploading && <div className="text-[11px] text-slate-500 mt-1" data-testid="dnr-uploading">Uploading…</div>}
                {dnrFile && <div className="text-[11px] text-emerald-700 mt-1" data-testid="dnr-file-attached">✓ {dnrFile.filename}</div>}
              </div>
              <div>
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea rows={2} value={dnrNotes} onChange={(e) => setDnrNotes(e.target.value)} placeholder="e.g. GP-signed 2026-08-01, kept in green folder" data-testid="dnr-notes" />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setDnrOpen(false); }}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-care-flags" className="bg-slate-900 hover:bg-slate-800">
              {saving ? "Saving…" : "Save flags"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProfileHeader({ client, allergies, onClientUpdate }) {
  const { user } = useAuth();
  if (!client) return (
    <div className="bg-white border-b border-slate-200 px-6 py-4 text-sm text-slate-500">Loading service user…</div>
  );
  const age = client.dob
    ? Math.floor((Date.now() - new Date(client.dob).getTime()) / (365.25 * 24 * 3600 * 1000))
    : null;
  const critical = (allergies || []).filter((a) => (a.active || "yes") === "yes" && ["severe", "life-threatening"].includes(a.severity));
  return (
    <div className="bg-white border-b border-slate-200 px-4 lg:px-6 py-4">
      <div className="flex items-start gap-3 lg:gap-4">
        <Link to="/clients" className="text-slate-500 hover:text-slate-900 mt-1 shrink-0" data-testid="btn-back-to-clients" title="Back to Service Users">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="relative group shrink-0" data-testid="client-avatar-wrap">
          <div className="lg:hidden"><PersonAvatar person={client} name={client.full_name} size="lg" testid="client-avatar-mobile" /></div>
          <div className="hidden lg:block"><PersonAvatar person={client} name={client.full_name} size="xl" testid="client-avatar" /></div>
          <button
            type="button"
            onClick={() => {
              const input = document.getElementById(`client-photo-input-${client.id}`);
              input?.click();
            }}
            className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center text-white text-[10px] uppercase tracking-widest font-semibold opacity-0 group-hover:opacity-100 transition"
            data-testid="client-avatar-edit"
            title="Change photo"
          >
            Change
          </button>
          <input
            id={`client-photo-input-${client.id}`}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid="client-photo-file"
            onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              if (!["image/jpeg","image/png","image/webp"].includes(f.type)) return toast.error("Use JPEG, PNG or WEBP.");
              if (f.size > 5 * 1024 * 1024) return toast.error("Photo must be 5MB or less.");
              const fd = new FormData(); fd.append("file", f);
              try {
                const r = await api.post(`/photos/client/${client.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                toast.success("Profile photo updated");
                onClientUpdate?.({ ...client, ...r.data });
              } catch (err) { toast.error(formatApiError(err)); }
              finally { e.target.value = ""; }
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-lg lg:text-xl font-semibold text-slate-900 truncate" data-testid="client-name">{client.full_name}</h1>
          {client.preferred_name && client.preferred_name !== client.full_name && (
            <div className="text-xs text-slate-500 mt-0.5">Preferred name: <span className="font-medium text-slate-700">{client.preferred_name}</span></div>
          )}
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            {client.dob && <span>DOB {client.dob}{age != null ? ` · ${age} yrs` : ""}</span>}
            {asArray(client.primary_needs).slice(0, 3).map((n) => (
              <span key={n} className="pill pill-info">{n}</span>
            ))}
            {asArray(client.primary_needs).length > 3 && (
              <span className="text-slate-500">+{asArray(client.primary_needs).length - 3}</span>
            )}
            {asArray(client.allergies).slice(0, 3).map((a) => (
              <span
                key={a}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${ALLERGY_SEVERITY_STYLES[getAllergySeverity(a)]}`}
              >
                <AlertTriangle className="w-3 h-3" /> {a}
              </span>
            ))}
            {asArray(client.allergies).length > 3 && (
              <span className="text-slate-500">+{asArray(client.allergies).length - 3}</span>
            )}
            {client.status && <StatusPill value={client.status} kind="generic" />}
          </div>
          {(client.care_flags && client.care_flags.length > 0) && (
            <div className="mt-2">
              <CareFlagsPills flags={client.care_flags} />
            </div>
          )}
          <ClientSiteMembership
            client={client}
            canManage={user?.role === "admin" || user?.role === "manager"}
            onSaved={(updated) => onClientUpdate?.({ ...client, ...updated })}
          />
        </div>
        <div className="shrink-0"><CareFlagsEditor client={client} onSaved={onClientUpdate} /></div>
      </div>
      {critical.length > 0 && (
        <div className="mt-3 bg-red-50 border border-red-400 border-l-4 border-l-red-600 p-3" data-testid="critical-allergy-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" strokeWidth={2.25} />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-red-800 font-bold" data-testid="critical-allergy-count">
                {critical.length} life-threatening / severe allerg{critical.length === 1 ? "y" : "ies"}
              </div>
              <ul className="mt-1 space-y-0.5">
                {critical.map((a) => (
                  <li key={a.id} className="text-sm text-red-900 font-medium" data-testid={`critical-allergy-${a.id}`}>
                    <span className="uppercase tracking-widest text-[10px] font-bold text-red-700 mr-1.5">{a.severity}</span>
                    {a.kind} · {a.item}
                    {a.reaction && <span className="font-normal"> — {a.reaction}</span>}
                    {a.management && <span className="text-xs text-red-800 block ml-1 mt-0.5">→ {a.management}</span>}
                  </li>
                ))}
              </ul>
            </div>
            <Link to={`/clients/${client.id}/allergies-diet`} className="text-[10px] uppercase tracking-widest text-red-900 hover:underline shrink-0" data-testid="critical-allergy-link">
              Manage →
            </Link>
          </div>
        </div>
      )}
      {(client.care_flags || []).includes("dnr") && client.dnr_evidence && (
        <div className="mt-3 bg-sky-50 border border-sky-300 border-l-4 border-l-sky-600 p-3 flex items-start gap-2" data-testid="dnr-evidence-banner">
          <ShieldAlert className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" strokeWidth={2.25} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-sky-800 font-bold">DNR on file · {client.dnr_evidence.typed_client_name}</div>
            <div className="text-xs text-sky-900 mt-0.5">
              Confirmed by <span className="font-semibold">{client.dnr_evidence.confirmed_by_name || client.dnr_evidence.confirmed_by}</span>
              {client.dnr_evidence.confirmed_at && <span> · {new Date(client.dnr_evidence.confirmed_at).toLocaleDateString()}</span>}
              {client.dnr_evidence.notes && <span className="block text-[11px] text-sky-800 mt-0.5">Note: {client.dnr_evidence.notes}</span>}
            </div>
          </div>
          {client.dnr_evidence.evidence_file_id ? (
            <a
              href={`${API_BASE}/uploads/${client.dnr_evidence.evidence_file_id}/download`}
              target="_blank" rel="noreferrer"
              className="text-[10px] uppercase tracking-widest text-sky-900 font-semibold hover:underline shrink-0"
              data-testid="dnr-view-order-link"
            >
              View signed order →
            </a>
          ) : (
            <span className="text-[10px] uppercase tracking-widest text-sky-700 shrink-0" data-testid="dnr-no-file">No file attached</span>
          )}
        </div>
      )}
    </div>
  );
}

function StatusCards({ clientId, client }) {
  const [state, setState] = useState({ loading: true });

  const load = useCallback(() => {
    if (!clientId) return () => {};
    let alive = true;
    Promise.all([
      api.get(`/clients/${clientId}/sections/care-planning`).catch(() => ({ data: [] })),
      api.get(`/clients/${clientId}/sections/medication`).catch(() => ({ data: [] })),
      api.get(`/clients/${clientId}/sections/alerts`).catch(() => ({ data: [] })),
      api.get(`/clients/${clientId}/sections/charts`).catch(() => ({ data: [] })),
      api.get(`/clients/${clientId}/sections/consent`).catch(() => ({ data: [] })),
      api.get(`/clients/${clientId}/vitals/latest`).catch(() => ({ data: { has_data: false } })),
    ]).then(([cp, med, al, obs, con, vt]) => {
      if (!alive) return;
      const now = new Date();
      const plans = cp.data || [];
      // Care plan status
      const nextPlanReview = plans
        .map((p) => p.next_review_date && new Date(p.next_review_date))
        .filter(Boolean)
        .sort((a, b) => a - b)[0];
      const planStatus = !plans.length
        ? { label: "Not set", tone: "slate" }
        : (nextPlanReview && nextPlanReview < now)
          ? { label: "Review overdue", tone: "red" }
          : (nextPlanReview && (nextPlanReview - now) / 86400000 < 30)
            ? { label: "Review due soon", tone: "amber" }
            : { label: "Up to date", tone: "emerald" };
      // Medication
      const meds = (med.data || []).filter((m) => (m.status || "active") === "active");
      // Alerts (risk assessments) — active only
      const activeAlerts = (al.data || []).filter((a) => (a.active || "yes") === "yes");
      const critical = activeAlerts.filter((a) => a.severity === "critical").length;
      // Next review — combine ACTIVE care plans + active risk assessments + consent.
      // Care plans use `next_review_date`; risk assessments (`client_alerts`) and
      // consent records use `review_date`. Only active/under_review plans and
      // active risks count so an archived plan can't hold the tile forever.
      const activePlanReviews = plans
        .filter((p) => !p.status || ["active", "under_review"].includes(p.status))
        .map((p) => p.next_review_date && new Date(p.next_review_date))
        .filter(Boolean);
      const riskReviews = activeAlerts
        .map((r) => r.review_date && new Date(r.review_date))
        .filter(Boolean);
      const consentReviews = (con.data || [])
        .map((c) => c.review_date && new Date(c.review_date))
        .filter(Boolean);
      const allReviews = [...activePlanReviews, ...riskReviews, ...consentReviews]
        .filter((d) => !Number.isNaN(d.getTime()))
        .sort((a, b) => a - b);
      const nextReview = allReviews[0];
      // Last observation — pick the most recent across charts + vitals so any
      // recorded observation refreshes the tile.
      const chartTimes = (obs.data || [])
        .map((o) => o.measured_at && new Date(o.measured_at))
        .filter(Boolean);
      const vitalsLatest = vt.data || { has_data: false };
      const vitalsMeasuredAt = vitalsLatest.measured_at ? new Date(vitalsLatest.measured_at) : null;
      const obsSorted = [...chartTimes, vitalsMeasuredAt]
        .filter(Boolean)
        .sort((a, b) => b - a);
      const lastObs = obsSorted[0];

      // Legacy relative-time helper kept for the Vitals tile subtext below.
      const humanAgo = (d) => {
        if (!d) return "None";
        const min = Math.floor((now - d) / 60000);
        if (min < 60) return `${min} min ago`;
        const h = Math.floor(min / 60);
        if (h < 24) return `${h} hr${h === 1 ? "" : "s"} ago`;
        const days = Math.floor(h / 24);
        return `${days} day${days === 1 ? "" : "s"} ago`;
      };

      // Observation label per spec: Today / Yesterday / N days ago / "12 Aug 2026".
      const observationLabel = (d) => {
        if (!d) return "No observations";
        const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
        const days = Math.floor((startOfDay(now) - startOfDay(d)) / 86400000);
        if (days <= 0) return "Today";
        if (days === 1) return "Yesterday";
        if (days < 7) return `${days} days ago`;
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
      };

      setState({
        loading: false,
        planStatus,
        medCount: meds.length,
        alertCount: activeAlerts.length,
        criticalCount: critical,
        nextReview,
        keyWorker: client?.key_worker,
        lastObs,
        lastObsLabel: observationLabel(lastObs),
        vitals: vitalsLatest,
        vitalsAgo: vitalsMeasuredAt ? humanAgo(vitalsMeasuredAt) : "None recorded",
      });
    });
    return () => { alive = false; };
  }, [clientId, client?.key_worker]);

  useEffect(() => {
    const cancel = load();
    // Auto-refresh when the tab regains focus so a newly-recorded observation
    // or an updated review date shows up without a full page reload.
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      cancel && cancel();
    };
  }, [load]);

  if (state.loading) {
    return <div className="px-6 pt-4 text-xs text-slate-500" data-testid="status-cards-loading">Loading status…</div>;
  }

  const tones = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-900",
    amber: "border-amber-300 bg-amber-50 text-amber-900",
    red: "border-red-300 bg-red-50 text-red-900",
    sky: "border-sky-300 bg-sky-50 text-sky-900",
    violet: "border-violet-300 bg-violet-50 text-violet-900",
    slate: "border-slate-200 bg-slate-50 text-slate-800",
  };

  const vitals = state.vitals || {};
  const vitalsTone = !vitals.has_data
    ? "slate"
    : vitals.flag === "critical" ? "red"
    : vitals.flag === "watch" ? "amber"
    : "emerald";
  const vitalsValue = !vitals.has_data
    ? "No readings"
    : vitals.flag === "critical"
      ? `${state.vitalsAgo} · ${(vitals.flags || []).length} critical`
      : vitals.flag === "watch"
        ? `${state.vitalsAgo} · watch`
        : `${state.vitalsAgo} · normal`;
  const cards = [
    { icon: ClipboardCheck, label: "Care Plan", value: state.planStatus.label, tone: state.planStatus.tone, testid: "sc-care-plan", to: `/clients/${clientId}/care-planning` },
    { icon: Pill, label: "Medication", value: `${state.medCount} active`, tone: state.medCount ? "sky" : "slate", testid: "sc-medication", to: `/clients/${clientId}/medication` },
    { icon: ShieldAlert, label: "Risks", value: `${state.alertCount} active${state.criticalCount ? ` · ${state.criticalCount} critical` : ""}`, tone: state.criticalCount ? "red" : (state.alertCount ? "amber" : "slate"), testid: "sc-risks", to: `/clients/${clientId}/alerts` },
    { icon: Activity, label: "Vitals", value: vitalsValue, tone: vitalsTone, testid: "sc-vitals", to: `/clients/${clientId}/vitals` },
    { icon: CalendarClock, label: "Next Review", value: state.nextReview ? (state.nextReview < new Date() ? `Overdue · ${state.nextReview.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : state.nextReview.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })) : "Not scheduled", tone: (state.nextReview && state.nextReview < new Date()) ? "red" : "violet", testid: "sc-next-review", to: `/clients/${clientId}/care-planning` },
    { icon: UserCheck, label: "Key Worker", value: state.keyWorker || "Unassigned", tone: state.keyWorker ? "sky" : "slate", testid: "sc-key-worker" },
    { icon: HeartPulse, label: "Last Observation", value: state.lastObsLabel, tone: !state.lastObs ? "slate" : ((new Date() - state.lastObs) / 3600000 < 24) ? "emerald" : "amber", testid: "sc-last-obs", to: `/clients/${clientId}/charts` },
  ];

  return (
    <div className="px-4 lg:px-6 pt-3 lg:pt-4 pb-3 bg-white border-b border-slate-100" data-testid="status-cards">
      {/* Mobile: intentional snap-carousel — ~2 cards + a controlled peek of
          the next. The strip itself scrolls (never the page); a subtle
          right-edge fade signals more content beyond the peek. */}
      <div className="lg:hidden relative">
        <div
          className="flex gap-3 -mx-4 px-4 overflow-x-auto snap-x snap-mandatory no-scrollbar scroll-smooth pb-1"
          data-testid="status-cards-mobile"
        >
          {cards.map((c) => {
            const Icon = c.icon;
            const inner = (
              <div className={`h-full border rounded-md ${tones[c.tone]} px-3 py-3 flex flex-col gap-0.5`}>
                <div className="flex items-center gap-1.5 opacity-80">
                  <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                  <span className="text-[10px] uppercase tracking-widest font-semibold truncate">{c.label}</span>
                </div>
                <div className="text-sm font-medium leading-tight mt-0.5 break-words">{c.value}</div>
              </div>
            );
            // 42vw keeps ~2 full cards visible on a 375-430 px viewport with
            // a small controlled peek of the third — this makes the carousel
            // affordance obvious without letting the peek eat card content
            // at 320 px (min-w keeps card readable) or ballooning at 430 px.
            const cardCls = "block basis-[42vw] shrink-0 grow-0 min-w-[150px] max-w-[190px] snap-start";
            return c.to ? (
              <Link key={`m-${c.label}`} to={c.to} data-testid={`m-${c.testid}`} className={cardCls}>{inner}</Link>
            ) : (
              <div key={`m-${c.label}`} data-testid={`m-${c.testid}`} className={cardCls}>{inner}</div>
            );
          })}
        </div>
        {/* Right-edge fade — subtle affordance that more cards exist */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-white to-transparent"
          data-testid="status-cards-fade"
        />
      </div>
      {/* Desktop / tablet grid unchanged */}
      <div className="hidden lg:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2.5">
        {cards.map((c) => {
          const Icon = c.icon;
          const inner = (
            <div className={`h-full border ${tones[c.tone]} px-3 py-2.5 flex flex-col gap-0.5 hover:shadow-sm transition-shadow`}>
              <div className="flex items-center gap-1.5 opacity-80">
                <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="text-[10px] uppercase tracking-widest font-semibold">{c.label}</span>
              </div>
              <div className="text-sm font-medium leading-tight mt-0.5">{c.value}</div>
            </div>
          );
          return c.to ? (
            <Link key={c.label} to={c.to} data-testid={c.testid} className="block">{inner}</Link>
          ) : (
            <div key={c.label} data-testid={c.testid}>{inner}</div>
          );
        })}
      </div>
    </div>
  );
}

function ProfileSubnav({ clientId, active }) {
  const navigate = useNavigate();
  const groups = CLIENT_SECTION_LIST.reduce((acc, s) => {
    (acc[s.group] ||= []).push(s);
    return acc;
  }, {});
  return (
    <>
      {/* Mobile / tablet: native select — one-hand friendly, respects section grouping via optgroup */}
      <div className="lg:hidden px-4 pt-3 pb-2 bg-white border-b border-slate-200 shrink-0" data-testid="profile-subnav-mobile">
        <label className="sr-only" htmlFor="profile-section-select">Profile section</label>
        <select
          id="profile-section-select"
          value={active}
          data-testid="profile-section-select"
          onChange={(e) => navigate(`/clients/${clientId}/${e.target.value}`)}
          className="w-full h-11 px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-400/40"
        >
          {Object.entries(groups).map(([group, items]) => (
            <optgroup key={group} label={group}>
              {items.map((s) => (
                <option key={s.key} value={s.key} data-testid={`profile-section-opt-${s.key}`}>{s.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Desktop side-nav */}
      <aside data-testid="profile-subnav" className="hidden lg:block w-56 shrink-0 h-full bg-white border-r border-slate-200 overflow-y-auto">
      {Object.entries(groups).map(([group, items]) => (
        <div key={group} className="py-2">
          <div className="px-4 py-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{group}</div>
          <ul>
            {items.map((s) => {
              const Icon = s.icon;
              const on = active === s.key;
              return (
                <li key={s.key}>
                  <Link
                    to={`/clients/${clientId}/${s.key}`}
                    data-testid={`profile-nav-${s.key}`}
                    className={`flex items-center gap-2 px-4 py-1.5 text-sm ${
                      on
                        ? "bg-sky-50 text-sky-800 border-l-2 border-sky-600 font-medium"
                        : "text-slate-700 hover:bg-slate-50 border-l-2 border-transparent"
                    }`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${s.color || "text-slate-500"}`} strokeWidth={1.9} />
                    <span className="truncate">{s.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </aside>
    </>
  );
}

function EmptyState({ label }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 p-16 text-center" data-testid="section-empty">
      <div className="text-sm text-slate-500">No {label.toLowerCase()} recorded yet.</div>
    </div>
  );
}

function SingletonView({ section, record }) {
  if (!record) {
    return (
      <div className="bg-white border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 max-w-3xl" data-testid="singleton-empty">
        No details recorded yet. Click <span className="font-medium">Add details</span> to get started.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 p-6 max-w-3xl" data-testid="singleton-view">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        {section.fields.map((f) => (
          <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
            <dt className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{f.label}</dt>
            <dd className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">
              {record[f.name] || <span className="text-slate-300">—</span>}
            </dd>
          </div>
        ))}
      </dl>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-6 pt-3 border-t border-slate-100">
        Last updated {record.updated_at?.slice(0, 19).replace("T", " ")} · {record.created_by_name}
      </div>
    </div>
  );
}

const ACTIVITY_STYLES = {
  "Water Refill":    { bg: "bg-sky-100",     text: "text-sky-700"    },
  "Getting Dressed": { bg: "bg-emerald-100", text: "text-emerald-700"},
  "Getting Washed":  { bg: "bg-cyan-100",    text: "text-cyan-700"   },
  "Oral Care":       { bg: "bg-teal-100",    text: "text-teal-700"   },
  "Meal":            { bg: "bg-orange-100",  text: "text-orange-700" },
  "Medication":      { bg: "bg-red-100",     text: "text-red-700"    },
  "Mood":            { bg: "bg-purple-100",  text: "text-purple-700" },
  "Toileting":       { bg: "bg-amber-100",   text: "text-amber-700"  },
  "Sleep":           { bg: "bg-indigo-100",  text: "text-indigo-700" },
  "Activity":        { bg: "bg-pink-100",    text: "text-pink-700"   },
  "Behaviour":       { bg: "bg-rose-100",    text: "text-rose-700"   },
  "Note":            { bg: "bg-slate-100",   text: "text-slate-700"  },
  "Other":           { bg: "bg-slate-100",   text: "text-slate-700"  },
};

function formatDay(day) {
  if (!day) return "";
  const d = new Date(day);
  if (isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

function TimelineList({ rows, section, onEdit, onDelete, canDelete }) {
  if (!rows.length) return <EmptyState label={section.label} />;
  const groups = {};
  rows.forEach((r) => {
    const day = (r.occurred_at || r.created_at || "").slice(0, 10);
    (groups[day] ||= []).push(r);
  });
  const dayKeys = Object.keys(groups).sort().reverse();
  return (
    <div className="max-w-3xl space-y-6" data-testid="timeline-view">
      {dayKeys.map((day) => (
        <div key={day}>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{formatDay(day)}</div>
          <ul className="space-y-2">
            {groups[day].map((r) => {
              const style = ACTIVITY_STYLES[r.activity] || ACTIVITY_STYLES.Other;
              const time = (r.occurred_at || "").slice(11, 16);
              return (
                <li key={r.id} data-testid={`timeline-item-${r.id}`} className="bg-white border border-slate-200 p-3 flex items-start gap-3 group">
                  <div className={`w-10 h-10 rounded-full ${style.bg} ${style.text} flex items-center justify-center text-[10px] font-semibold shrink-0 uppercase tracking-wider`}>
                    {(r.activity || "?").split(" ").map((w) => w[0]).join("").slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500 mb-0.5">
                      {time || "—"} · {r.activity} · <span className="text-slate-400">{r.created_by_name || r.created_by}</span>
                    </div>
                    <div className="text-sm font-medium text-slate-900">{r.title}</div>
                    {r.notes && <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words">{r.notes}</div>}
                  </div>
                  <div className="lg:opacity-0 lg:group-hover:opacity-100 flex items-center gap-0.5 shrink-0">
                    <button onClick={() => onEdit(r)} className="w-9 h-9 flex items-center justify-center rounded-md text-slate-500 hover:text-sky-700 hover:bg-slate-50" data-testid={`edit-${r.id}`} title="Edit"><Pencil className="w-4 h-4" /></button>
                    {canDelete && <button onClick={() => onDelete(r.id)} className="w-9 h-9 flex items-center justify-center rounded-md text-slate-500 hover:text-red-600 hover:bg-slate-50" data-testid={`delete-${r.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ListView({ rows, section, onEdit, onDelete, canDelete }) {
  if (!rows.length) return <EmptyState label={section.label} />;
  return (
    <div className="bg-white border border-slate-200 overflow-x-auto">
      <table className="data-table" data-testid="section-table">
        <thead>
          <tr>
            <th className="row-num">#</th>
            {section.columns.map((c, ci) => <th key={`${c.key}-${ci}`} className={c.numeric ? "text-right" : ""}>{c.label}</th>)}
            <th className="w-24 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} data-testid={`section-row-${r.id}`}>
              <td className="row-num">{i + 1}</td>
              {section.columns.map((c, ci) => {
                const v = r[c.key];
                if (c.status) return <td key={`${c.key}-${ci}`}><StatusPill value={v} kind={c.status} /></td>;
                if (c.render === "review_status") {
                  const status = computeReviewStatus(v);
                  const style = REVIEW_STATUS_STYLE[status];
                  return (
                    <td key={`${c.key}-${ci}`}>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border ${style.className}`}
                        data-testid={`review-status-${r.id}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        {style.label}
                      </span>
                    </td>
                  );
                }
                if (c.render === "priority") {
                  const cls = PRIORITY_STYLE[v] || "bg-slate-50 text-slate-500 border-slate-200";
                  return (
                    <td key={`${c.key}-${ci}`}>
                      {v ? <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${cls}`}>{v}</span> : <span className="text-slate-300">—</span>}
                    </td>
                  );
                }
                if (c.render === "progress") {
                  const cls = PROGRESS_STYLE[v] || "bg-slate-50 text-slate-500 border-slate-200";
                  return (
                    <td key={`${c.key}-${ci}`}>
                      {v ? <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${cls}`}>{v}</span> : <span className="text-slate-300">—</span>}
                    </td>
                  );
                }
                if (c.render === "version") {
                  return (
                    <td key={`${c.key}-${ci}`} className="tabular-nums text-xs text-slate-600">v{r.version || 1}</td>
                  );
                }
                const display = Array.isArray(v) ? v.join(", ") : v;
                return (
                  <td key={`${c.key}-${ci}`} className={c.numeric ? "text-right tabular-nums" : ""}>
                    {display != null && display !== "" ? String(display) : <span className="text-slate-300">—</span>}
                  </td>
                );
              })}
              <td className="text-right whitespace-nowrap">
                <button onClick={() => onEdit(r)} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`edit-${r.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                {canDelete && <button onClick={() => onDelete(r.id)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" data-testid={`delete-${r.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionView({ clientId, section, clientName }) {
  // Bespoke person-centred view for Important People (cards + drawer + tenant-scoped photos).
  if (section.key === "important-people") {
    return <ImportantPeopleView clientId={clientId} clientName={clientName} />;
  }
  // Bespoke Care Plan landing: 5 summary rows + linked-subsection detail sheet.
  // Falls back to GenericSectionView for clients that have no is_summary rows.
  if (section.key === "care-planning") {
    return <CarePlanView clientId={clientId} clientName={clientName} GenericSectionView={GenericSectionView} />;
  }
  return <GenericSectionView clientId={clientId} section={section} clientName={clientName} />;
}

function GenericSectionView({ clientId, section, clientName }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setRows([]);
    try {
      const r = await api.get(`/clients/${clientId}/sections/${section.key}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [clientId, section.key]);

  useEffect(() => { load(); }, [load]);

  const submit = async (values) => {
    try {
      if (editing) {
        await api.put(`/clients/${clientId}/sections/${section.key}/${editing.id}`, { data: values });
        toast.success("Updated");
      } else {
        await api.post(`/clients/${clientId}/sections/${section.key}`, { data: values });
        toast.success("Created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/clients/${clientId}/sections/${section.key}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const singleton = section.viewType === "singleton";
  const timeline = section.viewType === "timeline";
  const documentation = section.key === "documentation";
  const record = singleton ? rows[0] : null;
  const canDelete = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="flex-1 lg:min-h-0 flex flex-col lg:overflow-hidden bg-slate-50">
      <div className="min-h-12 border-b border-slate-200 bg-white px-4 lg:px-6 py-2 lg:py-0 flex items-center justify-between gap-2 shrink-0 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 hidden lg:block">{clientName || "Service User"}</div>
          <h2 className="font-heading text-sm lg:text-base font-semibold truncate" data-testid="section-title">{section.label}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-11 lg:h-9 text-slate-700" data-testid="section-refresh">
            <RefreshCw className="w-3.5 h-3.5 lg:mr-1.5" /> <span className="hidden lg:inline">Refresh</span>
          </Button>
          {section.key === "daily-logs" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportOpen(true)}
              className="h-11 lg:h-9 text-slate-700"
              data-testid="daily-notes-export-open"
            >
              <Download className="w-3.5 h-3.5 lg:mr-1.5" /> <span className="hidden lg:inline">Download</span>
            </Button>
          )}
          {singleton ? (
            <Button
              size="sm"
              onClick={() => { setEditing(record || null); setDialogOpen(true); }}
              className="h-11 lg:h-9 bg-sky-700 hover:bg-sky-800"
              data-testid="section-edit-singleton"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> {record ? "Edit details" : "Add details"}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              className="h-11 lg:h-9 bg-sky-700 hover:bg-sky-800"
              data-testid="section-new"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> {section.key === "daily-logs" ? "Add note" : "New Record"}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 lg:overflow-auto p-4 lg:p-6">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="section-loading">Loading…</div>
        ) : singleton ? (
          <SingletonView section={section} record={record} />
        ) : timeline ? (
          <TimelineList rows={rows} section={section} onEdit={(r) => { setEditing(r); setDialogOpen(true); }} onDelete={del} canDelete={canDelete} />
        ) : (
          <ListView rows={rows} section={section} onEdit={(r) => { setEditing(r); setDialogOpen(true); }} onDelete={del} canDelete={canDelete} />
        )}

        {documentation && (
          <div className="mt-6 max-w-3xl bg-white border border-slate-200 p-4">
            <FileAttachments module="clients" recordId={clientId} />
          </div>
        )}
      </div>

      <RecordDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        module={section}
        record={editing}
        onSubmit={submit}
        clientId={clientId}
      />
      {section.key === "daily-logs" && (
        <DailyNotesExportDialog
          open={exportOpen}
          onOpenChange={setExportOpen}
          clientId={clientId}
          clientName={clientName}
        />
      )}
    </div>
  );
}

export default function ClientProfile() {
  const { clientId, section: sectionKey } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const active = sectionKey && CLIENT_SECTIONS[sectionKey] ? sectionKey : "general";
  const section = CLIENT_SECTIONS[active];

  const [client, setClient] = useState(null);
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!sectionKey || !CLIENT_SECTIONS[sectionKey]) {
      navigate(`/clients/${clientId}/general`, { replace: true });
      return;
    }
    setLoading(true);
    setNotFound(false);
    Promise.all([
      api.get(`/clients/${clientId}/details`),
      api.get(`/clients/${clientId}/sections/allergies-diet`).catch(() => ({ data: [] })),
    ])
      .then(([r, a]) => { setClient(r.data); setAllergies(a.data || []); })
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [clientId, sectionKey, navigate]);

  if (notFound) {
    return (
      <div className="flex-1 p-8" data-testid="client-not-found">
        <div className="text-sm text-red-600">Service user not found.</div>
        <Link to="/clients" className="text-sm text-sky-700 mt-2 inline-block">← Back to Service Users</Link>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-y-auto lg:overflow-hidden" data-testid="client-profile">
      <ProfileHeader client={client} allergies={allergies} onClientUpdate={setClient} />
      {client && <StatusCards clientId={clientId} client={client} />}
      {client && <ClientOutcomesPanel clientId={clientId} />}
      {client && (
        <div className="px-6 py-3 bg-white border-b border-slate-100">
          <ClientGallery clientId={clientId} canManage={user?.role === "admin" || user?.role === "manager"} />
        </div>
      )}
      <div className="flex-1 lg:min-h-0 flex flex-col lg:flex-row lg:overflow-hidden">
        <ProfileSubnav clientId={clientId} active={active} />
        {loading ? (
          <div className="flex-1 p-8 text-sm text-slate-500">Loading profile…</div>
        ) : (
          <SectionView clientId={clientId} section={section} clientName={client?.full_name} />
        )}
      </div>
    </div>
  );
}
